import BnumElementInternal from 'components/bnum-element-states';
import { HTMLBnumFragment } from '../fragment/bnum-fragment';
import { Attr, CustomFire, Data, Define, SetAttr } from 'core/decorators';
import { EMPTY_STRING } from '@rotomeca/utils';
import { Nullable, Optional } from '@rotomeca/utils';
import { UI } from 'core/decorators/ui';
import {
	CheckedChangeEvent,
	CheckedChangeEventConstructorOption,
} from 'core/utils/events/CheckedChangeEvent';
import { Result, Risky } from '@rotomeca/rop';
import Log from 'core/utils/Log';
import { NonStd } from 'core/decorators/nonstd';
import { INPUT_BASE_STYLE } from '../input/bnum-input';
import style from './bnum-radio.less';
import { Listener } from 'core/decorators/listener';
import JsEvent from '@rotomeca/event';
import { TAG_RADIO } from 'core/utils/constants/tags';

//#region Utilities

/**
 * Vérifie si une valeur est null ou undefined.
 *
 * @category Input
 *
 * @template T - Le type de la valeur à vérifier
 * @param newVal - La valeur à tester
 * @returns `true` si la valeur est null ou undefined, `false` sinon
 *
 * @example
 * ```ts
 * isNullOrUndefined(null); // true
 * isNullOrUndefined(undefined); // true
 * isNullOrUndefined("test"); // false
 * ```
 */
function isNullOrUndefined<T>(newVal: Optional<Nullable<T>>) {
	return newVal === null || newVal === undefined;
}

/**
 * Vérifie si une clé correspond à une clé valide d'actions.
 *
 * @param key - La clé à vérifier
 * @returns `true` si la clé fait partie des actions disponibles, `false` sinon
 *
 * @remarks
 * Les clés valides sont : 'checked', 'value', 'name', 'disabled'
 */
function isOnActionKey(key: string): key is keyof OnActionsOptions {
	return ['checked', 'value', 'name', 'disabled'].includes(key);
}

/**
 * Ajoute un listener sur l'instance qui active l'évènement
 * @param event Evènement qui est initialisé
 * @param instance Elément qui contient l'évènement
 */
function onStateChangeInitializer(
	event: OnChangeEvent,
	instance: HTMLBnumRadio,
) {
	instance.addEventListener('bnum-radio:change', (e: Event) =>
		event.call(e as BnumRadioCheckedChangeEvent),
	);
}

//#endregion Utilities

//#region Internals Types

/**
 * Paramètres passés à un callback d'action.
 *
 * @remarks
 * Utilisé pour transmettre le nom et la valeur d'un attribut lors d'une mise à jour.
 */
type OnActionParam = {
	/** Le nom de l'attribut concerné */
	name: string;
	/** La valeur de l'attribut (peut être null) */
	val: Nullable<string>;
};

/**
 * Signature d'un callback d'action.
 *
 * @param option - Les paramètres de l'action
 * @returns Les paramètres modifiés après traitement
 *
 * @remarks
 * Permet d'intercepter et de modifier les paramètres avant qu'ils ne soient appliqués.
 */
type OnActionCallback = (option: OnActionParam) => OnActionParam;

/**
 * Options de callbacks pour les différentes actions possibles.
 *
 * @remarks
 * Chaque propriété correspond à un attribut qui peut être intercepté lors d'une mise à jour.
 */
type OnActionsOptions = {
	/** Callback exécuté lors de la modification de l'attribut 'checked' */
	checked?: OnActionCallback;
	/** Callback exécuté lors de la modification de l'attribut 'value' */
	value?: OnActionCallback;
	/** Callback exécuté lors de la modification de l'attribut 'name' */
	name?: OnActionCallback;
	/** Callback exécuté lors de la modification de l'attribut 'disabled' */
	disabled?: OnActionCallback;
};

/**
 * Détails additionnels pour l'événement de changement de l'état coché.
 */
type CheckedChangeEventDetails = {
	/** L'événement natif sous-jacent */
	inner: Event;
	/** Indique si l'événement peut se propager */
	bubbles: boolean;
	/** Indique si l'événement peut être annulé */
	cancelable: boolean;
};

/**
 * Options de construction pour l'événement de changement d'état coché du radio.
 *
 * @remarks
 * Spécialisation du type générique {@link CheckedChangeEventConstructorOption} pour le composant radio.
 */
type BnumRadioCheckedChangeEventConstructorOption =
	CheckedChangeEventConstructorOption<
		string,
		CheckedChangeEventDetails,
		HTMLBnumRadio
	>;

/**
 * Événement personnalisé déclenché lors du changement d'état d'un bouton radio.
 *
 * @remarks
 * Cet événement encapsule les informations sur le changement d'état (coché/décoché)
 * d'un élément radio personnalisé.
 */
export class BnumRadioCheckedChangeEvent extends CheckedChangeEvent<
	string,
	CheckedChangeEventDetails,
	HTMLBnumRadio
> {}

/**
 * Interface définissant les références aux éléments UI du composant radio.
 *
 * @remarks
 * Utilisé par le décorateur {@link UI} pour injecter les références typées
 * aux éléments du Shadow DOM. Ces références permettent d'interagir avec
 * l'input natif et les slots de contenu.
 */
type BnumRadioUi = {
	/** L'élément input radio natif */
	input: HTMLInputElement;
	/** Le slot contenant la légende principale du radio */
	slotLegend: HTMLSlotElement;
	/** Le slot contenant l'indice/aide du radio */
	slotHint: HTMLSlotElement;
};

type OnChangeCallback = (ev: BnumRadioCheckedChangeEvent) => void;
type OnChangeEvent = JsEvent<OnChangeCallback>;
//#endregion Internals Types

//#region Global Constants

/**
 * Identifiant de l'élément input radio interne.
 * @internal
 */
const ID_INPUT = 'radio';

/**
 * Nom de l'attribut 'checked'.
 * @internal
 */
const ATTRIBUTE_CHECKED = 'checked';

/**
 * Nom de l'attribut 'value'.
 * @internal
 */
const ATTRIBUTE_VALUE = 'value';

/**
 * Nom de l'événement 'change'.
 * @internal
 */
const EVENT_CHANGE = 'bnum-radio:change';

/**
 * Liste des attributs synchronisés entre l'élément hôte et l'input interne.
 *
 * @remarks
 * Ces attributs sont automatiquement propagés de l'élément personnalisé vers l'input natif.
 * @internal
 */
const SYNCED_ATTRIBUTES = ['name', 'checked', 'value', 'disabled'];

/**
 * Template HTML du composant radio.
 *
 * @remarks
 * Structure DOM utilisée pour créer le shadow DOM du composant.
 * Comprend un input radio natif et un label avec des slots pour le contenu et l'indice.
 * @internal
 */
const TEMPLATE = (
	<HTMLBnumFragment>
		<input type="radio" id={ID_INPUT} class="radio" />
		<label part="label" for="radio" class="radio__label">
			<span class="radio__label--legend">
				<slot id="legend"></slot>
			</span>
			<span class="radio--hint label-container--hint">
				<slot id="hint" name="hint"></slot>
			</span>
		</label>
	</HTMLBnumFragment>
);

//#endregion Global Constants

/**
 * Composant personnalisé représentant un bouton radio avec support de formulaire.
 *
 * @remarks
 * Ce composant Web étend {@link BnumElementInternal} et fournit un bouton radio personnalisé
 * avec support complet des formulaires HTML, gestion d'état et accessibilité.
 *
 * Le composant utilise le Shadow DOM pour encapsuler son style et sa structure,
 * et synchronise automatiquement ses attributs avec un input radio natif sous-jacent.
 *
 * @example
 * Structure simple :
 * ```html
 * <bnum-radio name="rotomeca" value="valeur 1">
 *   Mon élément
 * </bnum-radio>
 * ```
 *
 * @example
 * Structure avec indice :
 * ```html
 * <bnum-radio name="rotomeca" value="valeur 2">
 *   Mon élément
 *   <span slot="hint">Indice !</span>
 * </bnum-radio>
 * ```
 *
 * @fires BnumRadioCheckedChangeEvent - Déclenché lorsque l'état coché du radio change
 *
 * @public
 *
 * @structure Structure simple
 * <bnum-radio name="rotomeca" value="valeur 1">
 *   Mon élément
 * </bnum-radio>
 *
 * @structure Structure avec indice
 * <bnum-radio name="rotomeca" value="valeur 2">
 *   Mon élément
 *   <span slot="hint">Indice !</span>
 * </bnum-radio>
 *
 * @structure Disabled
 * <bnum-radio name="radio" value="valeur x" data-legend="Mon élément" data-hint="Indice !" checked disabled></bnum-radio>
 *
 * @slot (default) - Légende de l'élément
 * @slot hint - Aide supplémentaire dans la légende
 *
 * @event {CustomEvent<{ inner: BnumRadioCheckedChangeEvent }>} bnum-radio:change - Lorsque l'élément change d'état
 *
 * @attr {string} value - Valeur de l'élément
 * @attr {string} name - Nom de l'élément, permet de gérer les interactions des radio ayant le même nom
 * @attr {'disabled' | '' | undefined} (optional) disabled - Désactive l'élément
 * @attr {'' | undefined} (optional) checked - Si l'élément est actif ou non
 * @attr {string | undefined} (optional) data-legend - Label de l'élément. Est écraser si un slot est défini.
 * @attr {string | undefined} (optional) data-hint - Aide supplémentaire pour le label. Est écraser si un slot est défini.
 *
 * @cssvar {#000091} --bnum-radio-color - Couleur du radio
 * @cssvar {1rem} --bnum-radio-font-size - Taille du label principal
 * @cssvar {1px} --bnum-radio-border-size - Taille du countour du radio
 * @cssvar {50%} --bnum-radio-border-radius - "border-radius" de l'élément
 */
@Define({
	template: TEMPLATE,
	tag: TAG_RADIO,
	styles: [INPUT_BASE_STYLE, style],
})
export class HTMLBnumRadio extends BnumElementInternal {
	//#region Constants
	/**
	 * Indique que ce composant peut être associé à un formulaire.
	 *
	 * @remarks
	 * Permet au composant de participer au cycle de vie des formulaires HTML,
	 * notamment la soumission et la validation.
	 *
	 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/ElementInternals#instance_properties | ElementInternals}
	 */
	static readonly formAssociated = true;
	//#endregion Constants

	//#region Getters/Setters
	/**
	 * Références aux éléments du DOM interne.
	 *
	 * @remarks
	 * Injecté automatiquement par le décorateur {@link UI}.
	 * Fournit un accès typé à l'input radio natif et aux slots de contenu.
	 *
	 * @internal
	 */
	@UI({
		input: `#${ID_INPUT}`,
		slotLegend: '#legend',
		slotHint: '#hint',
	})
	accessor #_ui!: BnumRadioUi;

	/**
	 * Le nom du groupe de boutons radio.
	 *
	 * @remarks
	 * Les boutons radio partageant le même nom forment un groupe mutuellement exclusif.
	 * Un seul bouton peut être sélectionné à la fois dans un groupe.
	 *
	 * @defaultValue Chaîne vide
	 */
	@Attr() accessor name: string = EMPTY_STRING;

	/**
	 * La valeur associée au bouton radio.
	 *
	 * @remarks
	 * Cette valeur est envoyée lors de la soumission du formulaire si le radio est coché.
	 *
	 * @defaultValue Chaîne vide
	 */
	@Attr() accessor value: string = EMPTY_STRING;

	/**
	 * Indique si le bouton radio est coché.
	 *
	 * @remarks
	 * Contrôle l'état de sélection du bouton radio.
	 *
	 * @defaultValue `true`
	 */
	@Attr() accessor checked: boolean = true;

	/**
	 * Indique si le bouton radio est désactivé.
	 *
	 * @remarks
	 * Un bouton radio désactivé ne peut pas être sélectionné ni recevoir le focus.
	 *
	 * @defaultValue `false`
	 */
	@Attr() accessor disabled: boolean = false;

	/**
	 * Texte de la légende principale du bouton radio.
	 *
	 * @remarks
	 * Stocke le contenu textuel qui sera affiché comme label principal du radio.
	 * Cette propriété est en lecture seule (pas de setter) et est initialisée
	 * lors de la construction du composant.
	 *
	 * @defaultValue Chaîne vide
	 * @internal
	 */
	@Data({ setter: false }) accessor #_legend: string = EMPTY_STRING;

	/**
	 * Appelé lorsque l'état de l'élément change
	 */
	@Listener(onStateChangeInitializer) accessor onstatechange!: OnChangeEvent;

	/**
	 * Texte de l'indice/aide du bouton radio.
	 *
	 * @remarks
	 * Stocke le contenu textuel qui sera affiché comme information complémentaire.
	 * Cette propriété est en lecture seule (pas de setter) et est initialisée
	 * lors de la construction du composant.
	 *
	 * @defaultValue Chaîne vide
	 * @internal
	 */
	@Data({ setter: false }) accessor #_hint: string = EMPTY_STRING;

	/**
	 * Récupère l'input radio interne.
	 *
	 * @remarks
	 * Permet d'accéder à l'input radio natif pour des opérations spécifiques.
	 *
	 * @returns L'input radio interne
	 */
	get internalCheckbox(): HTMLInputElement {
		return this.#_ui.input;
	}
	//#endregion Getters/Setters

	//#region Lifecycle
	/**
	 * Constructeur du composant HTMLBnumRadio.
	 *
	 * @remarks
	 * Initialise l'instance du composant en appelant le constructeur parent.
	 */
	constructor() {
		super();
	}

	/**
	 * Attache un Shadow DOM au composant.
	 *
	 * @returns La racine du Shadow DOM créée
	 *
	 * @remarks
	 * Configure le Shadow DOM en mode 'open' avec délégation du focus.
	 * Cela permet au focus de se déplacer automatiquement vers l'input interne.
	 *
	 * @protected
	 * @override
	 */
	protected _p_attachCustomShadow(): Nullable<ShadowRoot> {
		return this.attachShadow({ mode: 'open', delegatesFocus: true });
	}

	/**
	 * Construit le DOM du composant après son attachement.
	 *
	 * @remarks
	 * Configure le rôle ARIA, initialise les écouteurs d'événements,
	 * initialise le contenu des slots et synchronise l'état initial avec les attributs.
	 *
	 * @protected
	 * @override
	 */
	@SetAttr('role', 'radio')
	protected _p_buildDOM(): void {
		this.#_setupListeners().#_init().#_sync();
	}

	/**
	 * Gère la mise à jour d'un attribut observé.
	 *
	 * @param name - Le nom de l'attribut modifié
	 * @param oldVal - L'ancienne valeur de l'attribut
	 * @param newVal - La nouvelle valeur de l'attribut
	 *
	 * @remarks
	 * Cette méthode est appelée automatiquement lorsqu'un attribut observé change.
	 * Elle détermine si une mise à jour est nécessaire et la déclenche si besoin.
	 *
	 * Pour l'attribut 'checked', compare l'état booléen plutôt que la chaîne.
	 * Pour l'attribut 'value', compare avec la valeur de l'input interne.
	 *
	 * @protected
	 * @override
	 */
	protected _p_update(
		name: string,
		oldVal: string | null,
		newVal: string | null,
	): void | Nullable<'break'> {
		let needUpdate = oldVal !== newVal;

		if (name === ATTRIBUTE_CHECKED) {
			const isChecked = this.#_ui.input.checked;
			const willBeChecked = newVal !== null && newVal !== 'false';
			needUpdate = isChecked !== willBeChecked;
		} else if (name === ATTRIBUTE_VALUE) {
			needUpdate = this.#_ui.input.value !== newVal;
		}

		if (needUpdate) {
			this.#_update(name, newVal, {
				checked: this.#_onUpdateChecked.bind(this),
			}).tapError(error =>
				Log.error('HTMLBnumRadio/_p_update', error.message, error),
			);
		}
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Update l'état du radio et déclenche l'événement bnum-radio:change
	 * @param checked - L'état à appliquer
	 */
	updateCheckAndFire(checked: boolean) {
		this.internalCheckbox.checked = checked;
		this.#_fireChange(new Event('change', { bubbles: true, composed: true }));
	}
	//#endregion Public methods

	//#region Private methods
	/**
	 * Initialise le contenu des slots avec les valeurs de légende et d'indice.
	 *
	 * @returns L'instance courante pour chaînage de méthodes
	 *
	 * @remarks
	 * Cette méthode remplit les slots du Shadow DOM avec le contenu textuel
	 * stocké dans les propriétés privées `#_legend` et `#_hint`.
	 * Elle n'affecte les slots que si les valeurs correspondantes sont définies.
	 *
	 * @private
	 */
	#_init() {
		const legend = this.#_legend;
		const hint = this.#_hint;

		const hasLegend = !!legend;
		const hasHint = !!hint;

		if (hasLegend) this.#_ui.slotLegend.innerText = legend;

		if (hasHint) this.#_ui.slotHint.innerText = hint;

		return this;
	}

	/**
	 * Callback exécuté lors de la mise à jour de l'attribut 'checked'.
	 *
	 * @param options - Les paramètres de l'action contenant le nom et la valeur
	 * @returns Les paramètres modifiés après traitement
	 *
	 * @remarks
	 * Met à jour l'état coché de l'input interne, l'attribut ARIA et la valeur du formulaire.
	 * Si le radio n'est pas coché, la valeur est définie à null pour le formulaire.
	 *
	 * @private
	 */
	#_onUpdateChecked(options: OnActionParam) {
		const { val: newVal } = options;
		const isChecked = !(isNullOrUndefined(newVal) || newVal === 'false');
		const input = this.#_ui.input;

		if (input.checked !== isChecked) {
			input.checked = isChecked;
		}

		this._p_internal.ariaChecked = String(isChecked);

		this.#_setFormValue(isChecked ? this.value : null);

		if (!isChecked) options.val = null;

		return options;
	}

	/**
	 * Met à jour un attribut de l'input interne avec gestion des callbacks.
	 *
	 * @param name - Le nom de l'attribut à mettre à jour
	 * @param newVal - La nouvelle valeur de l'attribut
	 * @param onactions - Callbacks optionnels à exécuter avant la mise à jour
	 * @returns Un {@link Result} indiquant le succès ou l'échec de l'opération
	 *
	 * @remarks
	 * Si un callback est défini pour l'attribut concerné, il est exécuté avant la mise à jour.
	 * Le callback peut modifier le nom et la valeur avant leur application.
	 *
	 * Si la nouvelle valeur est null ou undefined, l'attribut est supprimé de l'input.
	 *
	 * @private
	 */
	@Risky()
	#_update(
		name: string,
		newVal: Nullable<string>,
		onactions?: OnActionsOptions,
	): Result<void> {
		if (onactions && isOnActionKey(name)) {
			const callback = onactions[name as keyof OnActionsOptions];

			if (callback) {
				const plugin = callback({
					name,
					val: newVal,
				});

				if (plugin) {
					name = plugin.name;
					newVal = plugin.val;
				}
			}
		}

		if (isNullOrUndefined(newVal)) this.#_ui.input.removeAttribute(name);
		else this.#_ui.input.setAttribute(name, newVal!);

		return Result.Ok();
	}

	/**
	 * Configure l'écouteur d'événement pour les changements de l'input interne.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Écoute l'événement 'change' de l'input natif et déclenche l'événement personnalisé.
	 *
	 * @private
	 */
	#_handleInnerChange() {
		this.#_ui.input.addEventListener('change', ev => {
			this.#_fireChange(ev);
		});
		return this;
	}

	/**
	 * Déclenche l'événement personnalisé de changement d'état.
	 *
	 * @param ev - L'événement natif ayant déclenché le changement
	 * @returns Les options de construction de l'événement personnalisé
	 *
	 * @remarks
	 * Stoppe la propagation de l'événement natif, met à jour l'état interne,
	 * puis construit et déclenche un {@link BnumRadioCheckedChangeEvent}.
	 *
	 * Le décorateur {@link CustomFire} gère automatiquement la création et le dispatch
	 * de l'événement à partir des options retournées.
	 *
	 * @fires BnumRadioCheckedChangeEvent
	 * @private
	 */
	@CustomFire(BnumRadioCheckedChangeEvent)
	#_fireChange(ev: Event) {
		ev.stopPropagation();
		this.#_updateInternal();
		const details: CheckedChangeEventDetails = {
			inner: ev,
			bubbles: true,
			cancelable: true,
		};
		const options: BnumRadioCheckedChangeEventConstructorOption = {
			value: this.value,
			checked: this.checked,
			name: this.name,
			caller: this,
			details,
		};
		return options;
	}

	/**
	 * Met à jour l'état interne du composant à partir de l'input natif.
	 *
	 * @remarks
	 * Synchronise les propriétés `checked` et `value` du composant
	 * avec celles de l'input interne, et met à jour l'attribut ARIA correspondant.
	 *
	 * @private
	 */
	#_updateInternal() {
		const input = this.#_ui.input;
		this.checked = !!input.checked;
		this._p_internal.ariaChecked = this.checked as unknown as string;
		this.value = input.value;
	}

	/**
	 * Configure tous les écouteurs d'événements du composant.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Actuellement configure uniquement l'écouteur de changement de l'input interne.
	 *
	 * @private
	 */
	#_setupListeners() {
		this.#_handleInnerChange();
		return this;
	}

	/**
	 * Synchronise les attributs entre l'élément hôte et l'input interne.
	 *
	 * @returns L'instance courante pour chaînage
	 *
	 * @remarks
	 * Parcourt tous les {@link SYNCED_ATTRIBUTES} et applique leurs valeurs à l'input.
	 *
	 * Cas particulier : si l'attribut 'checked' n'est pas présent mais que la propriété
	 * `checked` est à `true`, l'attribut est défini explicitement.
	 *
	 * @private
	 */
	#_sync() {
		for (const attr of SYNCED_ATTRIBUTES) {
			if (this.hasAttribute(attr)) {
				this._p_update(attr, null, this.getAttribute(attr));
			} else {
				if (attr === ATTRIBUTE_CHECKED && this.checked) {
					this._p_update(ATTRIBUTE_CHECKED, null, 'true');
				}
			}
		}
		return this;
	}

	/**
	 * Définit la valeur du composant dans le formulaire parent.
	 *
	 * @param value - La valeur à définir (null si le radio n'est pas coché)
	 * @returns Un {@link Result} indiquant le succès de l'opération
	 *
	 * @remarks
	 * Utilise l'API ElementInternals pour intégrer le composant dans le système de formulaires.
	 * La valeur est null lorsque le radio n'est pas coché, et correspond à la propriété
	 * `value` lorsqu'il est coché.
	 *
	 * @private
	 */
	@Risky()
	#_setFormValue(value: Nullable<string>): Result<void> {
		this._p_internal.setFormValue(value);
		return Result.Ok();
	}
	//#endregion Private methods

	//#region Static
	/**
	 * Retourne la liste des attributs observés par le composant.
	 *
	 * @returns Un tableau contenant tous les noms d'attributs observés
	 *
	 * @remarks
	 * Combine les attributs observés du parent avec les {@link SYNCED_ATTRIBUTES} spécifiques
	 * à ce composant. Les changements de ces attributs déclencheront {@link _p_update}.
	 *
	 * @protected
	 * @static
	 * @override
	 * @deprecated Utilisez le décorateur {@link Observe} du commit 3e38db0162eef596874dbe32490d9e96b09fb1c0
	 * @see [feat(composants): ✨ Ajout d'un décorateur pour réduire le boilerplate des attibuts à observer](https://github.com/messagerie-melanie2/design-system-bnum/commit/3e38db0162eef596874dbe32490d9e96b09fb1c0)
	 */
	@NonStd('Deprecated')
	protected static _p_observedAttributes(): string[] {
		return [...super._p_observedAttributes(), ...SYNCED_ATTRIBUTES];
	}

	/**
	 * Retourne le nom de l'événement 'change'.
	 *
	 * @returns Le nom de l'événement 'change'
	 */
	public static get EVENT_CHANGE(): typeof EVENT_CHANGE {
		return EVENT_CHANGE;
	}
	//#endregion Static
}
